import { clsx } from 'clsx'
import { memo, useCallback, useEffect, useRef } from 'react'
import { registerCanvas, unregisterCanvas } from '@/lib/canvasRegistry'
import { rotatedSize } from '@/lib/geometry'
import type { SearchMatch } from '@/lib/pdf/text'
import type { PageRef } from '@/types'
import { PageLayers } from './PageLayers'
import { type OverlayHandle, PageOverlay } from './PageOverlay'

interface Props {
  page: PageRef
  index: number
  scale: number
  top: number
  left: number
  /** within the render window (canvas) */
  render: boolean
  /** close to the viewport (text + forms + overlay) */
  interactive: boolean
  hits: SearchMatch[] | undefined
  activeHit: SearchMatch | null
}

export const PageView = memo(function PageView({
  page,
  index,
  scale,
  top,
  left,
  render,
  interactive,
  hits,
  activeHit,
}: Props) {
  const { w, h } = rotatedSize(page)
  const pageRef = useRef<HTMLDivElement>(null)
  const overlay = useRef<OverlayHandle>(null)
  const getEl = useCallback(() => pageRef.current, [])
  const onRendered = useCallback((c: HTMLCanvasElement) => registerCanvas(page.id, c), [page.id])

  useEffect(() => () => unregisterCanvas(page.id), [page.id])

  const onPointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement
    const textLayer = pageRef.current?.querySelector('.textLayer')
    if (target.tagName === 'SPAN' && textLayer?.contains(target)) {
      textLayer.classList.add('selecting')
      const end = () => {
        textLayer.classList.remove('selecting')
        window.removeEventListener('pointerup', end)
      }
      window.addEventListener('pointerup', end)
      return
    }
    if (textLayer?.contains(target) || target === textLayer) overlay.current?.startArea(e.nativeEvent)
  }

  return (
    <div
      className="absolute"
      data-page-index={index}
      style={{ top, left, width: w * scale, height: h * scale }}
      role="group"
      aria-label={`Page ${index + 1}`}
    >
      <div
        ref={pageRef}
        data-page-id={page.id}
        className="folio-page absolute"
        onPointerDown={onPointerDown}
        style={{
          width: page.width * scale,
          height: page.height * scale,
          left: '50%',
          top: '50%',
          transform: `translate(-50%, -50%) rotate(${page.rotation}deg)`,
          ['--scale-factor' as string]: scale,
        }}
      >
        {render && page.srcId && (
          <PageLayers page={page} scale={scale} interactive={interactive} onRendered={onRendered} />
        )}
        {!render && page.srcId && <div className="absolute inset-0 animate-pulse bg-black/[0.02]" />}
        {hits && hits.length > 0 && (
          <div
            className="pointer-events-none absolute top-0 left-0 z-[5] origin-top-left"
            style={{ transform: `scale(${scale})` }}
          >
            {hits.map((m, i) =>
              m.rects.map((r, j) => (
                <div
                  key={`${i}-${j}`}
                  className={clsx('search-hit absolute', m === activeHit && 'active')}
                  style={{ left: r.x - 1, top: r.y - 1, width: r.w + 2, height: r.h + 2 }}
                />
              )),
            )}
          </div>
        )}
        {(render || !page.srcId) && <PageOverlay ref={overlay} page={page} scale={scale} pageEl={getEl} />}
      </div>
    </div>
  )
})
